import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../services/api';
import { readStashedToken } from '../../utils/sensitiveUrl';
import styles from './Unsubscribe.module.css';

/**
 * Landing page for the "Unsubscribe" link in product / campaign emails:
 *   {APP_WEB_URL}/unsubscribe?token=...
 * No login needed — the signed token can only turn product email off for its
 * own account. Transactional email (verification, password reset, security)
 * is not affected.
 */
export default function Unsubscribe() {
    const [searchParams] = useSearchParams();
    // Scrubbed from the address bar at boot (utils/sensitiveUrl.js) so analytics never sees it.
    const token = readStashedToken('token', '/unsubscribe') || searchParams.get('token');

    const [status, setStatus] = useState(token ? 'loading' : 'missing');
    const [info, setInfo] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const loadedRef = useRef(false);

    useEffect(() => {
        if (!token || loadedRef.current) return;
        loadedRef.current = true;
        apiRequest('/email-preferences/unsubscribe', { params: { token }, authenticated: false })
            .then((data) => { setInfo(data); setStatus('ready'); })
            .catch((err) => { setError(err?.message || 'This unsubscribe link is invalid.'); setStatus('failed'); });
    }, [token]);

    const confirm = async () => {
        setStatus('working');
        try {
            const data = await apiRequest('/email-preferences/unsubscribe', {
                method: 'POST', params: { token }, authenticated: false, retry: false,
            });
            setResult(data);
            setStatus('done');
        } catch (err) {
            setError(err?.message || 'Something went wrong. Please try again.');
            setStatus('failed');
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.card}>
                {status === 'loading' && (
                    <>
                        <div className={styles.spinner} />
                        <h1 className={styles.title}>Loading…</h1>
                    </>
                )}

                {status === 'ready' && info && (
                    <>
                        <span className={styles.icon}>✉️</span>
                        <h1 className={styles.title}>Unsubscribe from Aluvo product emails?</h1>
                        <p className={styles.body}>
                            {info.email ? <>For <strong>{info.email}</strong>. </> : null}
                            You'll stop receiving product updates, recommendations and campaign emails.
                            Account and security emails (like password resets) will still be sent.
                        </p>
                        {info.test && (
                            <p className={styles.feedbackOk} role="status">
                                This link came from a test email — it works, but nothing will be changed.
                            </p>
                        )}
                        {!info.productEmail && !info.test ? (
                            <p className={styles.feedbackOk} role="status">You're already unsubscribed from product emails.</p>
                        ) : (
                            <button className={styles.primaryBtn} onClick={confirm}>Unsubscribe</button>
                        )}
                    </>
                )}

                {status === 'working' && (
                    <>
                        <div className={styles.spinner} />
                        <h1 className={styles.title}>Updating your preferences…</h1>
                    </>
                )}

                {status === 'done' && (
                    <>
                        <span className={styles.icon}>✅</span>
                        <h1 className={styles.title}>{result?.test ? 'Test link works' : "You're unsubscribed"}</h1>
                        <p className={styles.body}>
                            {result?.test
                                ? 'In a real campaign this link turns product email off for the recipient. No changes were made.'
                                : "You won't receive Aluvo product emails anymore. You can turn them back on any time in your notification settings."}
                        </p>
                    </>
                )}

                {status === 'missing' && (
                    <>
                        <span className={styles.icon}>🔗</span>
                        <h1 className={styles.title}>No unsubscribe link found</h1>
                        <p className={styles.body}>Open the link straight from the email, or manage your emails in settings.</p>
                    </>
                )}

                {status === 'failed' && (
                    <>
                        <span className={styles.icon}>⌛</span>
                        <h1 className={styles.title}>That link didn't work</h1>
                        <p className={styles.body}>{error}</p>
                    </>
                )}

                <Link className={styles.browseLink} to="/settings/notifications">
                    Manage all email preferences →
                </Link>
            </div>
        </div>
    );
}
